import React, { useState, memo } from 'react'
import PropTypes from 'prop-types'
import { List, Map } from 'immutable'
import {
  formatPrice,
  replacePackage,
  QTY_SELECT,
  getTotal,
  getSize
} from '@aytm/app/swidget/questions/components/sh/functions'

import popupStyle from '@aytm/app/swidget/questions/styles/sh/popup.module.scss'
import ImageGallery from '@aytm/lib/rfd-ui/components/advanced/image-gallery'
import PopupImage from '@aytm/app/swidget/base/components/PopupImage.tsx'
import Select from '@aytm/lib/rfd-ui/components/basic/select'
import { asBoolean } from '@aytm/lib/immutable/ImmutableModel'
import Button from '@aytm/lib/rfd-ui/components/basic/button'

EcommercePopup.propTypes = {
  questionSchema: PropTypes.instanceOf(Map).isRequired,
  item: PropTypes.instanceOf(Map).isRequired,
  locale: PropTypes.instanceOf(Map).isRequired,
  answer: PropTypes.instanceOf(Map).isRequired,
  enableMagnify: PropTypes.bool,
  emulatedMobile: PropTypes.bool,
  onClosePopup: PropTypes.func,
  onAction: PropTypes.func
}

export function EcommercePopup(props) {
  const { questionSchema, item, answer, enableMagnify, onClosePopup, locale, onAction, emulatedMobile } = props
  const answerQty = answer.getIn(['answers', item.get('id'), 'count'])
  const findability =
    'find' == questionSchema.get('mode_type') ||
    (1 == questionSchema.get('max_cart_size') && !questionSchema.get('shopping_cart'))

  const maxQty = findability ? null : questionSchema.get('max_qty_per_product')
  const maxCartSize = findability ? null : questionSchema.get('max_cart_size')
  const maxMoney = findability ? null : questionSchema.get('max_money_spent')
  const total = getTotal(answer)
  const itemPrice = item.get('discounted_price') || item.get('price')
  const size = getSize(answer)
  const [volume, setVolume] = useState(item.getIn(['view3d', 'default']))

  const [picture, setPicture] = useState(null)
  const [qty, setQty] = useState(1)
  const gallery = (item.get('gallery') || List([])).insert(0, item.get('image'))
  const src = volume
    ? item.getIn(['view3d', 'file_url'])
    : picture || item.getIn(['currentPicture', 'original_url']) || item.getIn(['image', 'original_url'])
  const altText = src === item.getIn(['image', 'original_url']) ? item.getIn(['image', 'alt_text']) : undefined

  const onSelectQty = selectedQty => {
    setQty(selectedQty.get('value'))
  }

  const onButtonClick = () => {
    onAction(qty)
  }

  const onSetPicture = value => {
    setPicture(value)
    setVolume('view3d' == value)
  }

  return (
    <div className={popupStyle.ecommercePopup} data-testid="SW-SH-Popup">
      <div className={popupStyle.closeButton} onClick={onClosePopup} data-testid="SW-SH-Popup-Close">
        <i className="icon-aytmfd-close" />
      </div>
      <div className={popupStyle.lightboxContent}>
        {(item.get('gallery') || item.get('view3d')) && (
          <div className={popupStyle.smallPhotos} data-testid="SW-SH-Gallery">
            {(item.get('gallery') || item.get('view3d')) && (
              <ImageGallery
                imageList={
                  item.get('view3d') ? gallery.insert(1, item.get('view3d').set('original_url', 'view3d')) : gallery
                }
                onChange={onSetPicture}
                value={
                  volume
                    ? 1
                    : item.get('currentPicture')
                      ? item.getIn(['currentPicture', 'index']) + (item.get('view3d') ? 2 : 1)
                      : 0
                }
              />
            )}
          </div>
        )}
        <div className={popupStyle.imageContainer}>
          <PopupImage
            src={src}
            altText={altText}
            enableMagnify={enableMagnify}
            emulatedMobile={emulatedMobile}
            volume={volume}
          />
        </div>
        <div className={popupStyle.item} data-testid="SW-SH-Item">
          <div
            className={popupStyle.header}
            data-testid="SW-SH-Item-Header"
            dangerouslySetInnerHTML={{ __html: item.get('text') }}
          />
          <div
            className={popupStyle.text}
            data-testid="SW-SH-Item-Text"
            dangerouslySetInnerHTML={{ __html: item.get('description') }}
          />

          {!questionSchema.get('hide_price') && (
            <>
              <div
                className={popupStyle.price}
                data-testid="SW-SH-Item-Price"
                dangerouslySetInnerHTML={{
                  __html: item.get('discounted_price')
                    ? formatPrice(item.get('discounted_price'), questionSchema.get('price_mask'))
                    : formatPrice(item.get('price'), questionSchema.get('price_mask'))
                }}
              />
              {item.get('units') && <div className={popupStyle.units}> {item.get('units')} </div>}
            </>
          )}

          <span className={popupStyle.priceComments}>{replacePackage(item)}</span>
          <div className={popupStyle.buttons} data-testid="SW-SH-Item-Buttons">
            <Select
              className={popupStyle.combo}
              newIcon={true}
              options={QTY_SELECT.filter(
                v =>
                  (!maxQty || v.get('value') <= maxQty - answerQty) &&
                  (!maxCartSize || v.get('value') + size <= maxCartSize) &&
                  (!maxMoney || v.get('value') * itemPrice + total <= maxMoney)
              )}
              width={70}
              placeholder="1"
              value={qty}
              onSelect={onSelectQty}
              showSelected={true}
              disabled={findability}
              data-testid="SW-SH-Item-Select"
              // disabled={
              //   (asBoolean(maxQty) && maxQty <= answerQty + 1) ||
              //   (asBoolean(maxCartSize) && maxCartSize <= size + 1) ||
              //   (asBoolean(maxMoney) && maxMoney <= total + itemPrice)
              // }
            />
            <Button
              label={locale.getIn(['sh', 'addToCart'])}
              onAction={onButtonClick}
              className={popupStyle.button}
              data-testid="SW-SH-Item-AddButton"
              disabled={
                (asBoolean(maxQty) && maxQty <= answerQty) ||
                (asBoolean(maxCartSize) && maxCartSize <= size) ||
                (asBoolean(maxMoney) && maxMoney < total + itemPrice)
              }
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default memo(
  EcommercePopup,
  (prevProps, nextProps) =>
    prevProps.questionSchema === nextProps.questionSchema &&
    prevProps.item === nextProps.item &&
    prevProps.answer === nextProps.answer &&
    prevProps.enableMagnify === nextProps.enableMagnify &&
    prevProps.onClosePopup === nextProps.onClosePopup &&
    prevProps.locale === nextProps.locale &&
    prevProps.onAction === nextProps.onAction
)
